import { useAuth } from '@/auth/useAuth';
import { useWorkspaceCurrency, useWorkspaceTimeZone } from '@/features/workspace/useWorkspace';
import { useModules } from '@/modules/ModuleContext';
import { EmptyState, LinkButton, QueryError, Skeleton } from '@/ui';

import { firstNameOf, greetingFor, ledeFor } from './lede';
import { bookJobFor, nextActionFor, type NextAction } from './nextAction';
import { buildToday, type TodaySectionModel } from './todayModel';
import { TodaySection } from './TodaySection';
import { useWork } from './useWork';
import './today.css';

interface LedeProps {
  /** When the work was read, in epoch milliseconds: the moment the greeting is for. */
  readonly readAt: number;
  readonly total: number;
}

function Lede({ readAt, total }: LedeProps): React.JSX.Element {
  const { user } = useAuth();
  const zone = useWorkspaceTimeZone();
  const name = firstNameOf(user?.displayName ?? '');
  return <p className="fo-today__lede">{ledeFor(greetingFor(readAt, zone), name, total)}</p>;
}

interface TodayBodyProps {
  /** The sections to draw; undefined until the work has been read. */
  readonly sections: readonly TodaySectionModel[] | undefined;
  /** Whether the work could not be read, in which case the page says so and draws nothing else. */
  readonly failed: boolean;
  /** What to suggest when nothing is waiting. */
  readonly next: NextAction | undefined;
}

function TodayBody({ sections, failed, next }: TodayBodyProps): React.JSX.Element | null {
  if (sections === undefined) {
    return failed ? null : <Skeleton label="Loading what is waiting for you" lines={4} />;
  }
  if (sections.length === 0) {
    return (
      <EmptyState
        icon="check"
        title="Nothing is waiting for you"
        description="New work shows up here as soon as it needs you."
        {...(next === undefined ? {} : { action: next })}
      />
    );
  }
  return (
    <div className="fo-today__sections">
      {sections.map((section) => (
        <TodaySection key={section.key} section={section} />
      ))}
    </div>
  );
}

/**
 * Today: what is waiting for the signed-in person, most urgent first.
 *
 * The server decides what is waiting and for whom; this page words it and says where to go. The
 * heading stays while the work loads or fails, so the page is never without one, and so does the
 * way to book a job for the people who take calls: a failed list must not cost them the desk's
 * commonest task. When nothing is waiting the empty state offers the likeliest step instead, which
 * for the desk is that same booking, so the page never shows it twice.
 *
 * @returns the page
 */
export function TodayPage(): React.JSX.Element {
  const { can } = useAuth();
  const enabled = useModules();
  const zone = useWorkspaceTimeZone();
  const currency = useWorkspaceCurrency();
  const work = useWork();
  const { data, dataUpdatedAt } = work;

  // The moment is when the work was read, not the clock at render, so the sentences are a pure
  // function of what was fetched and agree with it until the next read.
  const sections =
    data === undefined ? undefined : buildToday(data, { now: dataUpdatedAt, zone, currency });
  const next = nextActionFor({ can, enabled });
  const booking = bookJobFor({ can, enabled });
  const emptyStateOffersIt = sections?.length === 0 && next?.to === booking?.to;
  const headerAction = emptyStateOffersIt ? undefined : booking;

  return (
    <div className="fo-today">
      <header className="fo-today__header">
        <div className="fo-today__heading">
          <h1>Today</h1>
          {data === undefined ? null : <Lede readAt={dataUpdatedAt} total={data.total} />}
        </div>
        {headerAction === undefined ? null : (
          <LinkButton variant="primary" icon="plus" to={headerAction.to}>
            {headerAction.label}
          </LinkButton>
        )}
      </header>
      <QueryError
        error={work.error}
        {...(data === undefined ? { subject: 'Your work' } : {})}
        onRetry={() => void work.refetch()}
        retrying={work.isFetching}
      />
      <TodayBody sections={sections} failed={work.isError} next={next} />
    </div>
  );
}
